import { GroupCellRendererParams, RowClassRules } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Center, Loader, UnstyledButton } from '@mantine/core';
import { BrokerNerdsBarkerCoreModelsInventoryListing } from '../../api';
import { BarkerEventListing } from '../../types';
import SearchIcon from '../../components/icons/Search';
import { GroupHeader } from './Inventory.GroupHeader';
import { useInventory } from './Inventory.hooks';
import { ErrorBoundary } from 'react-error-boundary';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../data/Global.state';
import { useFlag } from '@unleash/proxy-client-react';
import { useSetAtom } from 'jotai';
import { rowDraggingAtom } from '../../data/atoms';
import classes from './Inventory.styles.tsx.module.css';
import { useAppearanceSettings } from './Inventory.AppearanceSettings.hook';
import { BNEmptyState } from '../../components/EmptyState/EmptyState';
import { datadogRum } from '@datadog/browser-rum';

export default function Grid() {
  const { isAnimationEnabled, setIsAnimationEnabled } = useGlobalState('isAnimationEnabled', 'setIsAnimationEnabled');
  const {
    gridRef,
    mergedEventListings,
    columnDefs,
    onGridReady,
    onMasterRowDragEnd,
    onMasterRowDragMove,
    defaultColDef,
    detailCellRendererParams,
    groupSortOrderComparator,
    onCellClickedWhenDirty,
    trySaveGridState,
    inventoryQuickFilter,
    setInventoryQuickFilter,
    setFilter,
    soldInventoryDetailCellRendererParams,
    isShowingEventPerformance,
    onRowGroupToggled,
    onRowDataUpdated,
  } = useInventory(
    'gridRef',
    'mergedEventListings',
    'columnDefs',
    'onGridReady',
    'onMasterRowDragEnd',
    'onMasterRowDragMove',
    'defaultColDef',
    'detailCellRendererParams',
    'groupSortOrderComparator',
    'onCellClickedWhenDirty',
    'trySaveGridState',
    'inventoryQuickFilter',
    'setInventoryQuickFilter',
    'setFilter',
    'soldInventoryDetailCellRendererParams',
    'isShowingEventPerformance',
    'onRowGroupToggled',
    'onRowDataUpdated',
  );
  const [error, setError] = useState(0);

  const animationFlag = useFlag('use-animations');
  const appearanceSettings = useAppearanceSettings();

  // Stable reference: AG-Grid v35 calls redrawRows() whenever groupRowRenderer changes by
  // reference, which would tear down any open detail grid on every render. Keep it memoized.
  const groupRowRenderer = useCallback((params: GroupCellRendererParams<BrokerNerdsBarkerCoreModelsInventoryListing>) => {
    const [tenantId, eventId] = params.node.key!.split('|');
    return <GroupHeader tenantId={tenantId} eventId={eventId} />;
  }, []);

  // Declarative row classes. Previously this lived in getRowClass, which ran on every row as it
  // scrolled into view and did several document.querySelectorAll + classList mutations per row —
  // forcing synchronous layout during scroll (the cause of rows blanking out). rowClassRules lets
  // AG-Grid add/remove these classes natively (no DOM queries) and handles the stale-class cleanup
  // the old code did manually.
  //
  // The three highlight classes all paint the same ::before background, so they're kept mutually
  // exclusive in the original priority order (on-hold > at-floor > not-broadcasting) to preserve the
  // color an overlapping listing showed before. Highlights apply to master rows only; detail grids
  // set their own row classes via their own rowClassRules.
  const rowClassRules = useMemo<RowClassRules<BarkerEventListing>>(() => {
    const isOnHold = (data?: BarkerEventListing) => !!appearanceSettings?.highlightListingsOnHold && !!data && data.quantityReserved > 0;
    const isAtFloor = (data?: BarkerEventListing) => !!appearanceSettings?.highlightListingsAtFloor && !!data && data.pricerStatusId === 'AtFloor';
    const isNotBroadcasting = (data?: BarkerEventListing) => !!appearanceSettings?.highlightListingsNotBroadcasting && !!data && !data.isBroadcasting;
    return {
      'ag-row-created': (params) => isAnimationEnabled && !!params.data?.meta?.newRow,
      'ag-sales-container': (params) => !!params.node.detail && !!params.data && params.data.listingId.endsWith('sales'),
      'ag-row-listing-on-hold': (params) => !params.node.detail && isOnHold(params.data),
      'ag-row-listing-at-floor': (params) => !params.node.detail && isAtFloor(params.data) && !isOnHold(params.data),
      'ag-row-listing-not-broadcasting': (params) => !params.node.detail && isNotBroadcasting(params.data) && !isOnHold(params.data) && !isAtFloor(params.data),
    };
  }, [isAnimationEnabled, appearanceSettings]);

  function CustomNoRowsOverlay() {
    return (
      <Center pb="xl">
        <BNEmptyState
          title="No Results Found"
          description="Try adjusting the filter options"
          bottomContent={
            inventoryQuickFilter && (
              <UnstyledButton
                mt={10}
                onClick={() => {
                  setFilter('');
                  setInventoryQuickFilter('');
                }}
                className={classes.grayLink}
              >
                or clear quick filter
              </UnstyledButton>
            )
          }
          icon={<SearchIcon color="var(--colors-iconFill)" size={28} />}
          iconBg="var(--colors-opacity-hover)"
          h={320}
          w={320}
          border={false}
        />
      </Center>
    );
  }

  function CustomLoadingOverlay() {
    return (
      <div className={`ag-overlay-loading-center ${classes.agOverlayLoadingCenter}`}>
        <Loader color="var(--colors-gray-5)" type="dots" />
      </div>
    );
  }

  const setIsRowDragging = useSetAtom(rowDraggingAtom);

  // Remove any drag & drop classes when the drop happens outside of the grid or viewport
  useEffect(() => {
    const removeClasses = () => {
      // dir-down is used to determine drop point for details grid.
      // Delay removal of classes to allow the details grid to determine position
      // This is a catch all handler when dropped off the grid
      setTimeout(() => {
        document.querySelector('body')?.classList.remove('row-dragging');
        document.querySelector('.show-dropzone')?.classList.remove('show-dropzone');
        document.querySelector('.hover-over')?.classList.remove('hover-over');
        document.querySelector('.dir-up')?.classList.remove('dir-up');
        document.querySelector('.dir-down')?.classList.remove('dir-down');
        setIsRowDragging(false);
      }, 500);
    };
    window.addEventListener('mouseup', removeClasses);
    return () => {
      window.removeEventListener('mouseup', removeClasses);
    };
  }, [setIsRowDragging]);

  const enableCellTextSelection = useFlag('grid-text-selection');

  return (
    <div id="inventory-grid" className="ag-theme-alpine">
      <ErrorBoundary
        FallbackComponent={() => <div>Error</div>}
        resetKeys={[error]}
        onError={(_error) => {
          if (!import.meta.env.DEV) {
            datadogRum.addError(_error, { location: 'Inventory.Grid' });
            setTimeout(() => setError(error + 1), 250);
          }
        }}
      >
        <AgGridReact
          ref={gridRef}
          key={error}
          // ensureDomOrder // This could be useful when debugging
          // valueCache
          rowData={mergedEventListings}
          onRowDataUpdated={onRowDataUpdated}
          suppressScrollOnNewData
          suppressContextMenu
          columnDefs={columnDefs}
          onGridReady={onGridReady}
          rowSelection={{
            mode: 'singleRow', enableClickSelection: false, checkboxes: false
          }}
          onRowDragEnd={onMasterRowDragEnd}
          onRowGroupOpened={onRowGroupToggled}
          onRowDragMove={onMasterRowDragMove}
          postSortRows={(params) => {
            const index = params.nodes.findIndex((x) => x.data?.listingId.endsWith('sales'));
            if (index > -1) {
              const node = params.nodes.splice(index, 1)[0];
              params.nodes.push(node);
            }
          }}
          onFirstDataRendered={() => animationFlag && setIsAnimationEnabled(true)}
          onRowSelected={(params) => {
            const { rowIndex } = params;
            const el = document.querySelector(`[row-index="${rowIndex}"]`);
            if (params.data) {
              el?.setAttribute('aria-label', `Listing ${params?.data.listingId} row`);
            }
          }}
          detailRowAutoHeight
          rowBuffer={10}
          getRowId={(params) => params.data?.tenantIdListingId}
          masterDetail
          initialGroupOrderComparator={groupSortOrderComparator}
          groupDisplayType="groupRows"
          groupDefaultExpanded={1}
          onCellClicked={onCellClickedWhenDirty}
          detailCellRendererParams={isShowingEventPerformance ? soldInventoryDetailCellRendererParams : detailCellRendererParams}
          onColumnMoved={trySaveGridState}
          onColumnResized={trySaveGridState}
          onSortChanged={trySaveGridState}
          onColumnVisible={trySaveGridState}
          defaultColDef={defaultColDef}
          suppressHorizontalScroll={false}
          animateRows={isAnimationEnabled}
          rowClassRules={rowClassRules}
          enableCellTextSelection={enableCellTextSelection}
          suppressMoveWhenRowDragging
          headerHeight={32}
          isRowMaster={(data) => (!!data.ruleId && data.ruleCount && data.ruleCount > 1) || data.listingId.endsWith('sales') || false}
          getRowHeight={(params) => (params.node.group ? 56 : params.node?.data?.listingId.endsWith('sales') ? 1 : 36)}
          rowStyle={{
            lineHeight: '28px',
            fontSize: '12px',
          }}
          groupRowRenderer={groupRowRenderer}
          noRowsOverlayComponent={CustomNoRowsOverlay}
          loadingOverlayComponent={CustomLoadingOverlay}
          rowDragText={(params) => {
            const sectionName = params.rowNode?.data?.section;
            const rowName = params.rowNode?.data?.row;
            const seatsFrom = params.rowNode?.data?.seatFrom;
            const seatsTo = params.rowNode?.data?.seatThru;
            return `Section ${sectionName} · Row ${rowName} · Seats ${seatsFrom}-${seatsTo}`;
          }}
          tooltipShowDelay={1000}
        />
      </ErrorBoundary>
    </div>
  );
}
